import type { ReactElement } from "react";
import type { ContainmentPlanAction } from "@playbookai/widget-schemas";
import { widgetTargetKey } from "@playbookai/widget-schemas";
import { useFieldEditSession } from "../field-edit-session.js";

export function ContainmentActionDetailsFields({
  action,
  readOnly,
  onUpdate,
}: {
  action: ContainmentPlanAction;
  readOnly?: boolean | undefined;
  onUpdate: (patch: Partial<ContainmentPlanAction>) => void;
}): ReactElement {
  // PB-4923 AC5 — eight independent leaves on one containment action, each
  // addressed under the action's own stable id.
  const target = (field: string): string =>
    widgetTargetKey([
      { kind: "item", collection: "actions", itemId: action.id },
      { kind: "prop", name: field },
    ]);
  return (
    <details className="mt-2 rounded-md border border-pb-border bg-pb-card">
      <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-pb-text">
        Protection, verification and safeguards
      </summary>
      <div className="grid grid-cols-2 gap-3 border-t border-pb-border p-3">
        <TextField
          label="Protected scope"
          value={action.protectedScope ?? ""}
          maxLength={500}
          readOnly={readOnly}
          targetKey={target("protectedScope")}
          onChange={(value) => onUpdate({ protectedScope: value })}
        />
        <StartedOnField
          value={action.startedOn ?? ""}
          readOnly={readOnly}
          targetKey={target("startedOn")}
          onChange={(value) => onUpdate({ startedOn: value })}
        />
        <TextField
          label="Verification method"
          value={action.verificationMethod ?? ""}
          maxLength={500}
          readOnly={readOnly}
          targetKey={target("verificationMethod")}
          onChange={(value) => onUpdate({ verificationMethod: value })}
        />
        <TextField
          label="Verification cadence"
          value={action.verificationCadence ?? ""}
          maxLength={255}
          readOnly={readOnly}
          targetKey={target("verificationCadence")}
          onChange={(value) => onUpdate({ verificationCadence: value })}
        />
        <TextField
          label="Success criteria"
          value={action.successCriteria ?? ""}
          maxLength={500}
          readOnly={readOnly}
          multiline
          targetKey={target("successCriteria")}
          onChange={(value) => onUpdate({ successCriteria: value })}
        />
        <TextField
          label="Effectiveness evidence"
          value={action.effectivenessEvidence ?? ""}
          maxLength={500}
          readOnly={readOnly}
          multiline
          targetKey={target("effectivenessEvidence")}
          onChange={(value) => onUpdate({ effectivenessEvidence: value })}
        />
        <TextField
          label="Side-effect safeguard"
          value={action.sideEffectSafeguard ?? ""}
          maxLength={500}
          readOnly={readOnly}
          multiline
          targetKey={target("sideEffectSafeguard")}
          onChange={(value) => onUpdate({ sideEffectSafeguard: value })}
        />
        <TextField
          label="Escalation trigger"
          value={action.escalationTrigger ?? ""}
          maxLength={500}
          readOnly={readOnly}
          multiline
          targetKey={target("escalationTrigger")}
          onChange={(value) => onUpdate({ escalationTrigger: value })}
        />
      </div>
    </details>
  );
}

/** The one date control here; a native `type="date"` rather than `TextField`. */
function StartedOnField({
  value,
  readOnly,
  targetKey,
  onChange,
}: {
  value: string;
  readOnly?: boolean | undefined;
  targetKey: string;
  onChange: (value: string) => void;
}): ReactElement {
  const lease = useFieldEditSession(targetKey);
  return (
    <label className="text-xs text-pb-text-muted">
      Started on
      <input
        type="date"
        disabled={readOnly}
        value={value}
        {...lease}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 w-full rounded-md border border-pb-control-border bg-pb-card-elevated px-2 py-1.5 text-sm text-pb-text"
      />
    </label>
  );
}

function TextField({
  label,
  value,
  maxLength,
  readOnly,
  multiline = false,
  targetKey,
  onChange,
}: {
  label: string;
  value: string;
  maxLength: number;
  readOnly?: boolean | undefined;
  multiline?: boolean;
  /** PB-4923 AC5 — the merge-map leaf this field edits. */
  targetKey: string;
  onChange: (value: string) => void;
}): ReactElement {
  const className =
    "mt-1 w-full rounded-md border border-pb-control-border bg-pb-card-elevated px-2 py-1.5 text-sm text-pb-text";
  const lease = useFieldEditSession(targetKey);
  return (
    <label className="text-xs text-pb-text-muted">
      {label}
      {multiline ? (
        <textarea
          maxLength={maxLength}
          disabled={readOnly}
          value={value}
          {...lease}
          onChange={(event) => onChange(event.target.value)}
          className={`${className} min-h-16 resize-y`}
        />
      ) : (
        <input
          type="text"
          maxLength={maxLength}
          disabled={readOnly}
          value={value}
          {...lease}
          onChange={(event) => onChange(event.target.value)}
          className={className}
        />
      )}
    </label>
  );
}
